import React, { ChangeEvent, useCallback, useEffect, useReducer } from 'react'
import { FieldErrors, FieldName, UseFormMethods } from 'react-hook-form'
import { CircularProgress } from '@material-ui/core'
import { DocumentType } from '../../../lib/form'
import { fromAssetCreateRequest } from '../../../lib/upload/asset'
import { logAndCaptureException } from '../../../lib/monitoring'
import closeIcon from '../../../images/registration/close-circle.svg'
import fileIconBlue from '../../../images/registration/file-attach-icon-blue.svg'
import fileIconBlack from '../../../images/registration/file-attach-icon-black.svg'

import * as Styled from './styled'

export interface FileUploadProps<UploadFieldName extends string> {
  className?: string
  document?: DocumentType
  name: UploadFieldName
  setValue: UseFormMethods<Record<UploadFieldName, string>>['setValue']
  register: UseFormMethods<Record<UploadFieldName, string>>['register']
  errors?: FieldErrors
  required?: boolean
}

interface FileUploadState {
  dragActive: boolean
  uploadError: string
  uploadInput: File | undefined
  uploadedFile: File | undefined
  uploadedFileUrl: string
  uploading: boolean
  validationError: string
  fileSizeError: string
}

type FileUploadAction =
  | { type: 'drag_active' }
  | { type: 'drag_inactive' }
  | { type: 'unsupported_file_selected' }
  | { type: 'multiple_files_selected' }
  | { type: 'file_validated'; value: File }
  | { type: 'file_uploaded'; file: File; url: string }
  | { type: 'file_removed' }
  | { type: 'upload_error'; value: string }
  | { type: 'file_size_error'; value: string }

const reducer: React.Reducer<FileUploadState, FileUploadAction> = (prevState, action) => {
  switch (action.type) {
    case 'drag_active':
      return prevState.dragActive
        ? prevState
        : {
            ...prevState,
            dragActive: true,
          }
    case 'drag_inactive':
      return prevState.dragActive
        ? {
            ...prevState,
            dragActive: false,
          }
        : prevState
    case 'file_validated':
      return prevState.uploadError ||
        prevState.validationError ||
        prevState.uploadInput !== action.value
        ? {
            ...prevState,
            uploadError: '',
            uploadInput: action.value,
            uploading: true,
            validationError: '',
            fileSizeError: '',
          }
        : prevState
    case 'unsupported_file_selected':
      return {
        ...prevState,
        validationError: 'File type not supported. Accepted file types: PDF, JPG, PNG, and TIFF.',
      }
    case 'multiple_files_selected':
      return {
        ...prevState,
        validationError:
          'Uploading multiple files not supported. Please select a single file instead.',
      }
    case 'file_removed':
      return prevState.uploadInput ||
        prevState.uploadedFile ||
        prevState.validationError ||
        prevState.fileSizeError
        ? {
            ...prevState,
            uploadError: '',
            uploadInput: undefined,
            uploadedFile: undefined,
            uploadedFileUrl: '',
            validationError: '',
            fileSizeError: '',
          }
        : prevState
    case 'file_uploaded':
      return {
        ...prevState,
        uploading: false,
        uploadInput: undefined,
        uploadedFile: action.file,
        uploadedFileUrl: action.url,
      }
    case 'upload_error':
      return {
        ...prevState,
        uploadError: action.value,
        uploading: false,
      }
    case 'file_size_error':
      return {
        ...prevState,
        fileSizeError: action.value,
      }
    default:
      return prevState
  }
}

const allowedMimeTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/tiff']

export const FileUpload = <UploadFieldName extends string>({
  document,
  name,
  register,
  setValue,
  errors,
  required,
}: FileUploadProps<UploadFieldName>): React.ReactElement => {
  const [
    {
      dragActive,
      uploadError,
      uploadInput,
      uploadedFile,
      uploadedFileUrl,
      uploading,
      validationError,
      fileSizeError,
    },
    dispatch,
  ] = useReducer(reducer, null, () => ({
    dragActive: false,
    uploadError: '',
    uploadInput: undefined,
    uploadedFile: undefined,
    uploadedFileUrl: '',
    uploading: false,
    validationError: '',
    fileSizeError: '',
  }))

  useEffect(() => {
    if (!uploadInput) {
      return
    }
    const subscription = fromAssetCreateRequest(uploadInput).subscribe((result) => {
      if (result._tag === 'Left') {
        logAndCaptureException(result.error)
        dispatch({ type: 'upload_error', value: result.error.message })
      } else {
        dispatch({
          type: 'file_uploaded',
          file: uploadInput,
          url: result.data.uploadedFileUrl,
        })
      }
    })
    return () => subscription.unsubscribe()
  }, [uploadInput])

  useEffect(() => {
    if (!uploadedFileUrl) return
    // type cannot be inferred due to `IsFlatObject` check in `FieldName`
    setValue(name as FieldName<Record<UploadFieldName, string>>, uploadedFileUrl)
  }, [name, setValue, uploadedFileUrl])

  const inputRef = React.useRef<HTMLInputElement>(null)
  const outerRef = React.useRef<HTMLInputElement>(null)

  const handleDrag = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (e.type === 'dragenter' || e.type === 'dragover') {
      dispatch({ type: 'drag_active' })
      e.preventDefault()
      e.stopPropagation()
    } else if (e.type === 'dragend') {
      dispatch({ type: 'drag_inactive' })
      e.preventDefault()
      e.stopPropagation()
    } else if (e.type === 'dragleave' && e.target === outerRef.current) {
      dispatch({ type: 'drag_inactive' })
      e.preventDefault()
      e.stopPropagation()
    }
  }, [])

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    dispatch({ type: 'drag_inactive' })
    const { files } = e.dataTransfer
    if (files.length > 1) {
      dispatch({ type: 'multiple_files_selected' })
      return
    }
    const file = files[0]
    if (!file) {
      return
    }
    if (!allowedMimeTypes.includes(file.type)) {
      dispatch({ type: 'unsupported_file_selected' })
      return
    }
    // File Size in Bytes, 10MB = 10485760 Bytes
    if (file.size >= 10485760) {
      dispatch({ type: 'file_size_error', value: 'File size exceeds 10MB' })
      return
    }
    dispatch({ type: 'file_validated', value: file })
  }, [])

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    const { files } = e.target ?? {}
    if (!files || !files.length) {
      return
    }
    const file = files[0]
    if (!allowedMimeTypes.includes(file.type)) {
      dispatch({ type: 'unsupported_file_selected' })
      return
    }
    if (file.size >= 10485760) {
      dispatch({ type: 'file_size_error', value: 'File size exceeds 10MB' })
      return
    }
    dispatch({ type: 'file_validated', value: file })
  }, [])

  const onBrowseClick = useCallback(() => {
    inputRef?.current?.click()
  }, [])

  const onRemoveClick = useCallback(() => {
    dispatch({ type: 'file_removed' })
  }, [])

  const hasError = errors && !!errors[name]

  return (
    <>
      <Styled.OuterContainer
        $dragActive={dragActive}
        onClick={onBrowseClick}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        ref={outerRef}
      >
        <Styled.Title>
          <Styled.TitleHelper>
            {uploadedFileUrl
              ? 'Uploaded certificate'
              : `Please upload a copy of your ${document ?? 'credential(s)'} here`}
          </Styled.TitleHelper>
        </Styled.Title>
        <input
          type="hidden"
          name={name}
          ref={register ? register({ required }) : undefined}
          required={required}
        />
        <Styled.HiddenInput
          disabled={!!uploadedFile}
          ref={inputRef}
          type="file"
          multiple={false}
          accept=".pdf, .jpeg, .jpg, .png, .tif, .tiff"
          onChange={handleChange}
        />
        {uploading ? (
          <Styled.InnerContainer $dragActive={dragActive}>
            <CircularProgress size={30} color="inherit" />
          </Styled.InnerContainer>
        ) : (
          <Styled.InnerContainer
            $dragActive={dragActive}
            $fileSubmitted={!!uploadedFile}
            $fileSizeError={!!fileSizeError}
          >
            <Styled.Actions>
              <Styled.FileAttachIcon
                src={uploadedFile ? fileIconBlack : fileIconBlue}
                $fileSizeError={!!fileSizeError}
              />
              <Styled.DragText $fileSubmitted={!!uploadedFile} $fileSizeError={!!fileSizeError}>
                {uploadedFile
                  ? uploadedFile.name
                  : fileSizeError
                  ? fileSizeError
                  : 'Drag file here or click to browse'}
              </Styled.DragText>
              {(!!uploadedFile || !!fileSizeError) && (
                <Styled.RemoveFileButton onClick={onRemoveClick} type="button">
                  <Styled.CloseIcon src={closeIcon} />
                </Styled.RemoveFileButton>
              )}
            </Styled.Actions>
          </Styled.InnerContainer>
        )}
        <Styled.FileSizeText>Max. file size 10MB</Styled.FileSizeText>
      </Styled.OuterContainer>
      {hasError && (
        <Styled.Alert type="error">
          Remember to upload a copy of your credential(s) as they are required
        </Styled.Alert>
      )}
      {validationError && <Styled.Alert type="error">{validationError}</Styled.Alert>}
      {uploadError && (
        <Styled.Alert type="error">
          We were unable to upload your file, please try again soon.
        </Styled.Alert>
      )}
    </>
  )
}
